import { FLOW_ID_SESSION_KEY } from '@tn/foundation/utils/constants/common';
import { createConsoleLogger } from '@tn/foundation/utils/devtools/console-logger';
import { matchRelevantPath, RelevantPath } from '@tn/foundation/utils/matchRelevantPath';

import { FlowId } from '../types';

const { log } = createConsoleLogger('flows:helpers');

type GetAllRelevantFlowIdsArgs = {
    supportedFlows: FlowId[];
    pathname: string;
    getConfigByKey: (key: string) => unknown;
    region: string;
};

type FlowMatch = {
    id: FlowId;
    /**
     * True when every `RelevantPath` of this flow that matched `pathname` is marked
     * `protected: true`. Such a match is only honored if the user is already in the flow
     * (sessionStorage `FLOW_ID_SESSION_KEY` already holds the flow id).
     */
    isProtectedOnly: boolean;
};

const getAllRelevantFlowIds = ({
    supportedFlows,
    pathname,
    getConfigByKey,
    region,
}: GetAllRelevantFlowIdsArgs): FlowMatch[] => {
    return supportedFlows.reduce((acc, id) => {
        const relevantPaths = getConfigByKey(`${id}.relevantPaths`) as RelevantPath[] | undefined;
        if (!relevantPaths?.length) {
            log(`No relevant paths defined for flow ${id}`);
            return acc;
        }
        const matched = relevantPaths.filter((rp) =>
            matchRelevantPath(pathname, rp, region, getConfigByKey),
        );
        if (matched.length === 0) {
            return acc;
        }
        acc.push({
            id,
            isProtectedOnly: matched.every((rp) => rp.protected === true),
        });
        return acc;
    }, [] as FlowMatch[]);
};

const readSessionFlowId = (): FlowId | null => {
    if (typeof document === 'undefined') {
        return null;
    }
    return sessionStorage.getItem(FLOW_ID_SESSION_KEY) as FlowId | null;
};

export const decideFlowId = (
    pathname: string,
    getConfigByKey: (key: string) => unknown,
    region: string,
): FlowId | null => {
    const supportedFlows = getConfigByKey('flows.supportedFlows') as FlowId[];

    if (!supportedFlows) {
        log(`No flows are supported for ${pathname}.`);
        return null;
    }

    const matches = getAllRelevantFlowIds({ supportedFlows, pathname, getConfigByKey, region });

    if (!matches.length) {
        return handleNoMatch(pathname);
    }

    // Protected-only matches require the user to already be in the flow. Filter out any
    // protected-only match whose flow id is not in session — this is what prevents a cold
    // visit to a protected URL (e.g. `/account/confirm`) from binding the flow.
    const sessionFlowId = readSessionFlowId();
    const eligible = matches.filter(
        ({ id, isProtectedOnly }) => !isProtectedOnly || sessionFlowId === id,
    );

    if (!eligible.length) {
        log(`All matches for ${pathname} are protected-only and not authorized.`);
        return handleNoMatch(pathname);
    }

    const eligibleIds = eligible.map((m) => m.id);

    if (eligibleIds.length > 1) {
        const chosenFlowId = resolveFromSessionWhenMultiple(eligibleIds, pathname);
        if (chosenFlowId) {
            log(`Resolved multiple matching flows to ${chosenFlowId} from session storage.`);
            return chosenFlowId;
        }
    }

    if (sessionFlowId) {
        log(
            `Session flow ${sessionFlowId} is not eligible for ${pathname}, defaulting to ${eligibleIds[0]}.`,
        );
    } else {
        log(`No flow in session storage, defaulting to ${eligibleIds[0]}.`);
    }
    if (typeof document !== 'undefined') {
        sessionStorage.setItem(FLOW_ID_SESSION_KEY, eligibleIds[0]);
    }

    return eligibleIds[0];
};

/**
 * Returns the flow id that "wants" the given pathname only via a `protected: true`
 * `RelevantPath`, when no non-protected flow also matches. Used by `useFlowRouteGuard` to
 * detect a cold visit to a flow-only page so it can redirect the user away.
 *
 * - Returns `null` if no flow matches, or if any matching flow matches via a non-protected
 *   entry (because then the path is reachable on its own merit).
 * - Returns the first protected-only matching flow id otherwise.
 */
export const getProtectedFlowIdForPath = (
    pathname: string,
    getConfigByKey: (key: string) => unknown,
    region: string,
): FlowId | null => {
    const supportedFlows = getConfigByKey('flows.supportedFlows') as FlowId[];
    if (!supportedFlows) {
        return null;
    }
    const matches = getAllRelevantFlowIds({ supportedFlows, pathname, getConfigByKey, region });
    if (!matches.length) {
        return null;
    }
    if (matches.some((m) => !m.isProtectedOnly)) {
        return null;
    }
    return matches[0].id;
};

const handleNoMatch = (pathname: string): null => {
    log(`No flow matched for pathname ${pathname}`);
    if (typeof sessionStorage !== 'undefined') {
        sessionStorage.removeItem(FLOW_ID_SESSION_KEY);
    }
    return null;
};

const resolveFromSessionWhenMultiple = (flowIds: FlowId[], pathname: string): FlowId | null => {
    log(`Multiple flows matched for pathname ${pathname}, ${flowIds.join(', ')}`);
    if (typeof document !== 'undefined') {
        const flowId = sessionStorage.getItem(FLOW_ID_SESSION_KEY);
        if (flowId && flowIds.includes(flowId as FlowId)) {
            return flowId as FlowId;
        }
    }
    return null;
};
